import { useMemo, useRef, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { USD, compact, magnitude } from '../../lib/format';
import { C, ink } from '../../lib/theme';
import type { TendenciaRow } from '../../lib/types';
import { ChartEmpty, ChartTooltip, Legend, axisStyle, bucketLabel, gridStroke, useCrosshair } from './shared';

/**
 * Gráfico 1 — Tendência de perda (PRD §9.1). A v1 do PRD pedia hachura pra
 * separar auditado de pendente dentro da mesma barra; a correção de viabilidade
 * do próprio PRD manda usar DUAS BARRAS lado a lado (Auditado / Pendente), com
 * a paleta de event_type dentro de cada uma — igualmente legível e sem SVG
 * customizado. É o que está implementado aqui.
 *
 * É também o único gráfico que recebe brush-to-zoom (§9-D): arrastar sobre o
 * eixo X recorta o período, equivalente a mexer no Period Selector.
 */
export function TendenciaChart({ rows, granularity, onBrush }: {
  rows: TendenciaRow[];
  granularity: string;
  onBrush?: (from: string, to: string) => void;
}) {
  const crosshair = useCrosshair();
  const [dragFrom, setDragFrom] = useState<string | null>(null);
  const [dragTo, setDragTo] = useState<string | null>(null);
  const buckets = useRef<string[]>([]);

  const data = useMemo(() => {
    const map = new Map<string, {
      bucket: string; label: string;
      churn_aud: number; contraction_aud: number; churn_pend: number; contraction_pend: number;
    }>();
    for (const r of rows) {
      const key = String(r.bucket);
      if (!map.has(key)) {
        map.set(key, {
          bucket: key, label: bucketLabel(key, granularity),
          churn_aud: 0, contraction_aud: 0, churn_pend: 0, contraction_pend: 0,
        });
      }
      const slot = map.get(key)!;
      const field = `${r.event_type}_${r.auditado ? 'aud' : 'pend'}` as
        'churn_aud' | 'contraction_aud' | 'churn_pend' | 'contraction_pend';
      slot[field] += magnitude(r.value);
    }
    const out = [...map.values()].sort((a, b) => a.bucket.localeCompare(b.bucket));
    buckets.current = out.map((o) => o.bucket);
    return out;
  }, [rows, granularity]);

  if (data.length === 0) return <ChartEmpty />;

  function finishBrush() {
    if (dragFrom && dragTo && dragFrom !== dragTo && onBrush) {
      const a = buckets.current.indexOf(dragFrom);
      const b = buckets.current.indexOf(dragTo);
      const [lo, hi] = a < b ? [dragFrom, dragTo] : [dragTo, dragFrom];
      // O bucket final vira o ÚLTIMO dia daquele período, não o primeiro —
      // senão o range recortado perderia o próprio bucket em que o usuário soltou.
      const end = new Date(hi);
      if (granularity === 'month') end.setUTCMonth(end.getUTCMonth() + 1, 0);
      else if (granularity === 'week') end.setUTCDate(end.getUTCDate() + 6);
      onBrush(lo.slice(0, 10), end.toISOString().slice(0, 10));
    }
    setDragFrom(null); setDragTo(null);
  }

  return (
    <>
      <ResponsiveContainer width="100%" height="88%">
        <BarChart
          data={data} margin={{ top: 6, right: 6, left: -14, bottom: 0 }} barGap={2} barCategoryGap="22%"
          onMouseMove={(s) => {
            const label = (s as { activeLabel?: string }).activeLabel;
            const row = data.find((d) => d.label === label);
            crosshair.set(row?.bucket ?? null);
            if (dragFrom && row) setDragTo(row.bucket);
          }}
          onMouseLeave={() => { crosshair.set(null); setDragFrom(null); setDragTo(null); }}
          onMouseDown={(s) => {
            const label = (s as { activeLabel?: string }).activeLabel;
            const row = data.find((d) => d.label === label);
            if (row && onBrush) setDragFrom(row.bucket);
          }}
          onMouseUp={finishBrush}
        >
          <CartesianGrid stroke={gridStroke} vertical={false} />
          <XAxis dataKey="label" tick={axisStyle} axisLine={false} tickLine={false} />
          <YAxis tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={(v) => compact(v)} width={52} />
          <Tooltip
            cursor={{ fill: ink(0.05) }}
            content={({ active, payload, label }) =>
              active && payload?.length ? (
                <ChartTooltip
                  label={String(label)}
                  rows={payload
                    .filter((p) => Number(p.value) > 0)
                    .map((p) => ({ name: String(p.name), value: USD(p.value as number), color: String(p.color) }))}
                />
              ) : null
            }
          />
          {/* `maxBarSize`: sem ele, com poucos buckets (ex: 1 mês só no filtro) o
              Recharts estica a banda de cada categoria pra ocupar o espaço
              disponível inteiro — barra gigante e desproporcional. Um teto
              fixo mantém a barra com espessura razoável não importa quantos
              buckets existam. */}
          <Bar dataKey="churn_aud" stackId="aud" name="churn auditado" fill={C.churn} radius={[0, 0, 0, 0]} maxBarSize={64} />
          <Bar dataKey="contraction_aud" stackId="aud" name="contraction auditado" fill={C.amber} radius={[4, 4, 0, 0]} maxBarSize={64} />
          <Bar dataKey="churn_pend" stackId="pend" name="churn pendente" fill="rgba(242,85,90,0.42)" maxBarSize={64} />
          <Bar dataKey="contraction_pend" stackId="pend" name="contraction pendente" fill="rgba(245,165,36,0.42)" radius={[4, 4, 0, 0]} maxBarSize={64} />
          {dragFrom && dragTo && (
            <ReferenceArea
              x1={data.find((d) => d.bucket === dragFrom)?.label}
              x2={data.find((d) => d.bucket === dragTo)?.label}
              fill={C.accent} fillOpacity={0.14} stroke={C.accent} strokeOpacity={0.4}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
      <Legend items={[
        { label: 'churn auditado', color: C.churn },
        { label: 'contraction auditado', color: C.amber },
        { label: 'churn pendente', color: 'rgba(242,85,90,0.42)' },
        { label: 'contraction pendente', color: 'rgba(245,165,36,0.42)' },
      ]} />
      {onBrush && (
        <div style={{ fontSize: 10.5, color: 'rgba(253,250,249,0.28)', textAlign: 'center', marginTop: 4 }}>
          arraste sobre o eixo pra recortar o período
        </div>
      )}
    </>
  );
}
